import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jsPDF } from 'jspdf';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import logoFull from '../../assets/images/logo_aktivity.png';
import { sessionService, participantService } from '../../services';
import tokenStorage from '../../utils/tokenStorage';
import { getFeedbackRating, calculateAverageRating } from '../../utils/ratingUtils';

/**
 * Individual Participant Report Page
 * Based on the Aktivity report format - shows engagement, challenges & action plan summary
 */

const IndividualReportPage = () => {
  const { sessionId, participantId } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [participantData, setParticipantData] = useState(null);
  const [activityResponses, setActivityResponses] = useState([]);
  const [selectedSubSession, setSelectedSubSession] = useState(null);

  // Fetch real data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Get current participant from dual storage (auto-restores from sessionStorage if purged)
        const currentParticipant = tokenStorage.getParticipantData(sessionId) || {};
        // Support both new (participantId) and old (id) field names
        const targetParticipantId = participantId || currentParticipant.participantId || currentParticipant.id;

        // Fetch session details
        const sessionResponse = await sessionService.getSession(sessionId);
        if (sessionResponse.success) {
          setSessionData(sessionResponse.data);
        }

        // Fetch participant data and responses
        if (targetParticipantId) {
          try {
            const participantResponse = await participantService.getParticipantReport(sessionId, targetParticipantId);
            if (participantResponse.success) {
              setParticipantData(participantResponse.data?.participant || currentParticipant);
              setActivityResponses(participantResponse.data?.responses || []);
            }
          } catch {
            // Fall back to current participant data
            setParticipantData(currentParticipant);
          }
        } else {
          setParticipantData(currentParticipant);
        }

        // Also try to get activity responses from session data
        if (sessionResponse.data?.activityResponses) {
          const myResponses = sessionResponse.data.activityResponses.filter(
            r => r.participantId === targetParticipantId ||
                 r.participantId === currentParticipant.participantId ||
                 r.participantId === currentParticipant.id ||
                 r.participant === targetParticipantId
          );
          if (myResponses.length > 0) {
            setActivityResponses(prev => prev.length > 0 ? prev : myResponses);
          }
        }
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId, participantId]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-500">Loading report data...</p>
        </div>
      </div>
    );
  }

  // Build participant info from real data
  const participant = {
    name: participantData?.name || 'Participant',
    email: participantData?.email || '',
    organization: participantData?.organization || sessionData?.organizationName || '',
    program: sessionData?.name || 'Training Session',
    facilitator: sessionData?.trainerName || (sessionData?.trainer ? `${sessionData.trainer.firstName} ${sessionData.trainer.lastName}` : 'Trainer'),
  };

  // Calculate overall stats from real data
  const totalSubSessions = sessionData?.subSessions?.length || 1;

  // Get all activities from session
  const allActivities = sessionData?.subSessions?.flatMap(sub => sub.activities || []) || sessionData?.activities || [];
  const totalActivities = allActivities.length;

  // Get launched activities (activities that have been shown to participants)
  const launchedActivities = allActivities.filter(a => a.launched || a.status === 'launched' || a.status === 'completed' || a.status === 'closed').length || totalActivities;

  // Build a more robust set of responded activity IDs - handle various ID formats
  // This matches the logic in ParticipantReportsPage.jsx for consistency
  const respondedActivityIds = new Set();
  const respondedActivityTitles = new Set(); // Track by title (what backend returns)
  const respondedBySubSession = new Map(); // Map of subSessionId -> Set of (activityId or title)

  activityResponses.forEach(r => {
    // Get the subSessionId for this response
    const subSessionId = r.subSessionId?.toString() || r.subSession?._id?.toString() || '';

    // Initialize set for this subsession if not exists
    if (!respondedBySubSession.has(subSessionId)) {
      respondedBySubSession.set(subSessionId, { ids: new Set(), titles: new Set() });
    }
    const subSessionData = respondedBySubSession.get(subSessionId);

    // Add activity ID in various formats
    if (r.activityId) {
      const idStr = r.activityId.toString();
      respondedActivityIds.add(idStr);
      subSessionData.ids.add(idStr);
      // Also try removing ObjectId wrapper if present
      if (idStr.includes('ObjectId')) {
        const cleanId = idStr.replace(/ObjectId\(['"]?([^'"]+)['"]?\)/, '$1');
        respondedActivityIds.add(cleanId);
        subSessionData.ids.add(cleanId);
      }
    }
    if (r.activity?._id) {
      const idStr = r.activity._id.toString();
      respondedActivityIds.add(idStr);
      subSessionData.ids.add(idStr);
    }
    if (r.activity?.id) {
      const idStr = r.activity.id.toString();
      respondedActivityIds.add(idStr);
      subSessionData.ids.add(idStr);
    }
    // Handle activity field as string ID (MongoDB ObjectId as string)
    if (r.activity && typeof r.activity === 'string') {
      respondedActivityIds.add(r.activity);
      subSessionData.ids.add(r.activity);
    }
    // Track by title (backend returns 'title', not 'name')
    if (r.title) {
      const title = r.title.toLowerCase().trim();
      respondedActivityTitles.add(title);
      subSessionData.titles.add(title);
    }
    if (r.activityName || r.name || r.activity?.name || r.activity?.title) {
      const title = (r.activityName || r.name || r.activity?.name || r.activity?.title).toLowerCase().trim();
      respondedActivityTitles.add(title);
      subSessionData.titles.add(title);
    }
    // Also track by activityTitle field (common in backend responses)
    if (r.activityTitle) {
      const title = r.activityTitle.toLowerCase().trim();
      respondedActivityTitles.add(title);
      subSessionData.titles.add(title);
    }
  });

  // Check if ANY responses have subSessionId data - use strict matching if so
  const hasSubSessionData = activityResponses.some(r => r.subSessionId || r.subSession?._id);

  // For backwards compatibility
  const completedActivityIds = [...respondedActivityIds];
  const completedActivities = respondedActivityIds.size;

  // Calculate engagement score (based on launched activities, not total)
  const engagementScore = launchedActivities > 0
    ? Math.round((completedActivities / launchedActivities) * 100)
    : participantData?.engagementScore || 0;

  // Get action plans count
  const actionPlanResponses = activityResponses.filter(r =>
    r.type?.toLowerCase() === 'actionplans' || r.type?.toLowerCase() === 'action_plans'
  );
  const totalActionPlans = actionPlanResponses.length;

  // Get unique topics from challenges/action plans
  const uniqueTopics = [...new Set([
    ...activityResponses.filter(r => r.type?.toLowerCase() === 'challenges').map(r => r.topic || 'General'),
    ...actionPlanResponses.map(r => r.topic || 'General'),
  ])].length || 0;

  // Calculate session rating from feedback (handles emoji ratings)
  const feedbackResponses = activityResponses.filter(r => r.type?.toLowerCase() === 'feedback');

  // Extract rating from feedback responses - handle multiple possible formats
  let sessionRating = 0;
  if (feedbackResponses.length > 0) {
    let ratingSum = 0;
    let ratingCount = 0;

    feedbackResponses.forEach(r => {
      const resp = r.response;
      let rating = null;

      // Check response object for rating in various formats
      if (resp) {
        // Direct rating number on response
        if (typeof resp.rating === 'number' && resp.rating >= 1 && resp.rating <= 5) {
          rating = resp.rating;
        }
        // Score field
        else if (typeof resp.score === 'number' && resp.score >= 1 && resp.score <= 5) {
          rating = resp.score;
        }
        // Value field
        else if (typeof resp.value === 'number' && resp.value >= 1 && resp.value <= 5) {
          rating = resp.value;
        }
        // Emoji field - convert to rating
        else if (resp.emoji) {
          rating = getFeedbackRating(resp.emoji);
        }
        // Response is directly an emoji string
        else if (typeof resp === 'string') {
          rating = getFeedbackRating(resp);
        }
        // Response is directly a number
        else if (typeof resp === 'number' && resp >= 1 && resp <= 5) {
          rating = resp;
        }
      }

      // Also check rating directly on response object (r.rating instead of r.response.rating)
      if (!rating && r.rating && typeof r.rating === 'number' && r.rating >= 1 && r.rating <= 5) {
        rating = r.rating;
      }

      // Also check emoji directly on response object
      if (!rating && r.emoji) {
        rating = getFeedbackRating(r.emoji);
      }

      if (rating && rating > 0) {
        ratingSum += rating;
        ratingCount++;
      }
    });

    sessionRating = ratingCount > 0 ? Math.round((ratingSum / ratingCount) * 10) / 10 : 0;
  }

  // Fallback to participant data if no feedback responses found
  if (sessionRating === 0) {
    if (participantData?.sessionRating) {
      sessionRating = getFeedbackRating(participantData.sessionRating);
    } else if (participantData?.engagement?.sessionRating) {
      sessionRating = getFeedbackRating(participantData.engagement.sessionRating);
    }
  }

  const overallStats = {
    modulesCount: totalSubSessions,
    engagementScore,
    activitiesCompleted: completedActivities,
    activitiesLaunched: launchedActivities,
    totalActivities,
    totalActionPlans,
    totalTopics: uniqueTopics,
    sessionRating: sessionRating ? sessionRating.toFixed(1) : '0',
  };

  // Build session-wise participation from real data
  const sessionParticipation = sessionData?.subSessions?.map((subSession, idx) => {
    const subSessionId = (subSession._id || subSession.id)?.toString() || '';
    const subSessionData = respondedBySubSession.get(subSessionId);
    const subSessionActivityIds = (subSession.activities || []).map(a => String(a._id || a.id));
    const subSessionResponses = activityResponses.filter(r =>
      subSessionActivityIds.includes(String(r.activityId)) || String(r.subSessionId) === subSessionId
    );

    // Map activities to completion status
    const activities = (subSession.activities || []).map(activity => {
      const activityId = String(activity._id || activity.id);
      const actTitle = (activity.name || activity.title || '').toLowerCase().trim();
      const actType = (activity.type || '').toLowerCase();

      // Check subsession-specific matching (handles same-titled activities in different subsessions)
      const hasRespondedInSubSession = subSessionData && (
        subSessionData.ids.has(activityId) ||
        (actTitle && subSessionData.titles.has(actTitle))
      );

      // Fallback: Check global matching (for backwards compatibility when NO responses have subSessionId)
      const hasRespondedById = respondedActivityIds.has(activityId);
      const hasRespondedByTitle = actTitle && respondedActivityTitles.has(actTitle);
      const hasRespondedGlobally = hasRespondedById || hasRespondedByTitle;

      // Use strict subsession matching if responses have subSessionId data, otherwise fallback to global
      const hasRespondedFromAPI = hasSubSessionData ? hasRespondedInSubSession : hasRespondedGlobally;

      // Also check for response in the filtered subSessionResponses (legacy matching)
      const response = subSessionResponses.find(r =>
        String(r.activityId) === activityId ||
        (r.title && r.title.toLowerCase().trim() === actTitle) ||
        (r.activityTitle && r.activityTitle.toLowerCase().trim() === actTitle) ||
        (r.type?.toLowerCase() === actType && (
          r.activityName === activity.name ||
          r.title === activity.name ||
          r.activityTitle === activity.name
        ))
      );

      // Also check localStorage for stored responses (handles offline/reconnection scenarios)
      const localStorageKeys = [
        `poll_${sessionId}_${activityId}`,
        `quiz_${sessionId}_${activityId}`,
        `wordcloud_${sessionId}_${activityId}`,
        `feedback_${sessionId}_${activityId}`,
        `challenge_${sessionId}_${activityId}`,
        `actionplan_${sessionId}_${activityId}`,
        `openquestion_${sessionId}_${activityId}`,
        `responded_${sessionId}_${activityId}`,
        `poll_response_${sessionId}_${activityId}`,
      ];
      const hasLocalStorageResponse = localStorageKeys.some(key => {
        try {
          return localStorage.getItem(key);
        } catch { return false; }
      });

      // Combined check: API response OR localStorage response
      const isCompleted = hasRespondedFromAPI || response || hasLocalStorageResponse;

      // Check if activity was launched by trainer
      const isLaunched = activity.launchedAt ||
        ['active', 'launched', 'completed', 'ended', 'in_progress'].includes(activity.status);

      // Determine status: completed (responded), not_attended (launched but didn't respond), not_launched (never launched)
      let actStatus = 'not_launched';
      let detail = 'Not Launched';
      if (isCompleted) {
        actStatus = 'completed';
        detail = 'Completed';
      } else if (isLaunched) {
        actStatus = 'not_attended';
        detail = 'Not Attended';
      }

      return {
        name: activity.name || activity.title || activity.type,
        status: actStatus,
        detail,
      };
    });

    // Get takeaways from workbook/reflection responses
    const reflectionResponses = subSessionResponses.filter(r =>
      ['workbook', 'reflection', 'openquestion', 'open_question'].includes(r.type?.toLowerCase())
    );
    const keyTakeaways = reflectionResponses.slice(0, 3).map(r =>
      r.takeaway || r.reflection || r.response || r.text || 'Reflection submitted'
    );

    // Get subsession timing
    const startedAt = subSession.startedAt || subSession.launchedAt;
    const endedAt = subSession.endedAt || subSession.completedAt;

    return {
      id: subSession._id || subSession.id,
      day: idx + 1,
      topic: subSession.name || subSession.title,
      activities: activities.length > 0 ? activities : [{ name: 'No activities', detail: 'N/A' }],
      keyTakeaways: keyTakeaways.length > 0 ? keyTakeaways : ['No takeaways recorded'],
      startedAt,
      endedAt,
      status: subSession.status,
      description: subSession.description,
    };
  }) || [{
    day: 1,
    topic: 'Session',
    activities: [{ name: 'No activities', detail: 'N/A' }],
    keyTakeaways: ['No takeaways recorded'],
  }];

  // Build personal challenges from real responses
  const challengeResponses = activityResponses.filter(r => r.type?.toLowerCase() === 'challenges');
  const challengesByTopic = challengeResponses.reduce((acc, r) => {
    const topic = r.topic || 'General';
    if (!acc[topic]) acc[topic] = [];
    const challengeText = r.challenge || r.text || r.response;
    if (challengeText) acc[topic].push(challengeText);
    return acc;
  }, {});

  const personalChallenges = Object.entries(challengesByTopic).map(([topic, challenges]) => ({
    topic,
    challenges,
  }));

  // Build personal action plans from real responses
  const actionPlansByTopic = actionPlanResponses.reduce((acc, r) => {
    const topic = r.topic || 'General';
    if (!acc[topic]) acc[topic] = [];
    const actionText = r.plan || r.action || r.text || r.response;
    if (actionText) acc[topic].push(actionText);
    return acc;
  }, {});

  const personalActionPlans = Object.entries(actionPlansByTopic).map(([topic, actions]) => ({
    topic,
    actions,
  }));

  // Auto-generate personal recommendations based on session content
  const generatePersonalRecommendations = () => {
    const recommendations = [];

    // Get all unique topics from subsessions
    const sessionTopics = sessionData?.subSessions?.map(ss => ss.name || ss.title).filter(Boolean) || [];

    // Recommendation based on engagement score
    if (engagementScore < 50) {
      recommendations.push('Focus on active participation in upcoming sessions');
    } else if (engagementScore >= 80) {
      recommendations.push('Continue your excellent engagement pattern in future programs');
    }

    // Recommendations based on session topics
    sessionTopics.forEach(topic => {
      if (topic.toLowerCase().includes('influence') || topic.toLowerCase().includes('leadership')) {
        recommendations.push(`Attend Advanced ${topic} Workshop`);
      } else if (topic.toLowerCase().includes('communication') || topic.toLowerCase().includes('conflict')) {
        recommendations.push(`Use the provided templates for ${topic.toLowerCase()} conversations`);
      } else if (topic.toLowerCase().includes('stakeholder')) {
        recommendations.push(`Practice stakeholder mapping techniques from ${topic}`);
      }
    });

    // Recommendations based on challenges submitted
    if (personalChallenges.length > 0) {
      const challengeTopics = personalChallenges.map(c => c.topic).slice(0, 2);
      challengeTopics.forEach(topic => {
        if (topic !== 'General') {
          recommendations.push(`Review materials on ${topic} to address your identified challenges`);
        }
      });
    }

    // Recommendations based on action plans
    if (totalActionPlans > 0) {
      recommendations.push('Schedule weekly check-ins to track your action plan progress');
    }

    // Add peer learning recommendation
    recommendations.push('Join a peer Learning Circle for accountability');

    // Add workbook recommendation if available
    if (sessionData?.workbooks?.length > 0 || sessionData?.subSessions?.some(ss => ss.workbook)) {
      recommendations.push('Complete all workbook exercises and reflections');
    }

    // Default recommendations if none generated
    if (recommendations.length < 3) {
      recommendations.push('Review session materials and key takeaways');
      recommendations.push('Apply learnings in daily work scenarios');
      recommendations.push('Connect with fellow participants for knowledge sharing');
    }

    // Return unique recommendations (max 5)
    return [...new Set(recommendations)].slice(0, 5);
  };

  const personalRecommendations = sessionData?.recommendations?.length > 0
    ? sessionData.recommendations
    : generatePersonalRecommendations();

  // Calculate follow-up dates based on session duration
  const calculateFollowUpDates = () => {
    // Get session end date (or use last subsession end, or scheduled end, or current date)
    const sessionEndDate = sessionData?.endTime
      || sessionData?.actualEndTime
      || sessionData?.scheduledEndTime
      || sessionData?.subSessions?.slice(-1)[0]?.endedAt
      || sessionData?.subSessions?.slice(-1)[0]?.completedAt
      || sessionData?.completedAt
      || new Date();

    const endDate = new Date(sessionEndDate);

    // Calculate session duration in days
    const sessionStartDate = sessionData?.startTime
      || sessionData?.actualStartTime
      || sessionData?.scheduledDate
      || sessionData?.scheduledStartTime
      || sessionData?.subSessions?.[0]?.startedAt
      || sessionData?.createdAt;

    let durationDays = 1;
    if (sessionStartDate) {
      const startDate = new Date(sessionStartDate);
      durationDays = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
    }

    // Also consider number of subsessions as proxy for program length
    const numSubSessions = sessionData?.subSessions?.length || 1;

    // Calculate follow-up intervals based on duration
    // Short programs (1-2 days): +7 days, +21 days
    // Medium programs (3-5 days): +14 days, +30 days
    // Long programs (6+ days or 4+ subsessions): +21 days, +45 days
    let firstReviewDays, secondReviewDays;

    if (durationDays <= 2 && numSubSessions <= 2) {
      // Short programs
      firstReviewDays = 7;
      secondReviewDays = 21;
    } else if (durationDays <= 5 && numSubSessions <= 4) {
      // Medium programs
      firstReviewDays = 14;
      secondReviewDays = 30;
    } else {
      // Long programs
      firstReviewDays = 21;
      secondReviewDays = 45;
    }

    const firstReviewDate = new Date(endDate);
    firstReviewDate.setDate(firstReviewDate.getDate() + firstReviewDays);

    const secondReviewDate = new Date(endDate);
    secondReviewDate.setDate(secondReviewDate.getDate() + secondReviewDays);

    return {
      firstReview: {
        days: firstReviewDays,
        date: firstReviewDate,
      },
      secondReview: {
        days: secondReviewDays,
        date: secondReviewDate,
      },
    };
  };

  const nextFollowUp = calculateFollowUpDates();

  // Generate PDF Report
  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;

      // Colors
      const primaryColor = [255, 138, 0];
      const darkColor = [30, 30, 30];
      const grayColor = [100, 100, 100];
      const blueColor = [37, 99, 235];

      let yPos = margin;

      // Helper function for page breaks
      const checkPageBreak = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // === HEADER ===
      doc.setFillColor(...blueColor);
      doc.rect(0, 0, pageWidth, 8, 'F');

      yPos = 20;
      doc.setFontSize(14);
      doc.setTextColor(...darkColor);
      doc.setFont('helvetica', 'bold');
      doc.text('INDIVIDUAL PARTICIPANT REPORT – Engagement, Challenges & Action Plan Summary', margin, yPos);

      yPos += 8;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...grayColor);
      doc.text('(This is what each participant receives automatically)', margin, yPos);

      yPos += 12;

      // Participant Info
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkColor);

      const infoItems = [
        { icon: '🚀', label: 'Participant Name:', value: participant.name, bold: true },
        { icon: '📧', label: 'Email:', value: participant.email },
        { icon: '🏢', label: 'Organization:', value: participant.organization },
        { icon: '📋', label: 'Program:', value: participant.program },
        { icon: '👤', label: 'Facilitator:', value: participant.facilitator },
      ];

      infoItems.forEach((item) => {
        doc.setFont('helvetica', item.bold ? 'bold' : 'normal');
        doc.text(`${item.label} ${item.value}`, margin, yPos);
        yPos += 6;
      });

      yPos += 8;

      // === 1. OVERALL PARTICIPATION SNAPSHOT ===
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text('1. OVERALL PARTICIPATION SNAPSHOT', margin, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const stats = [
        `Modules in Session: ${overallStats.modulesCount}`,
        `Overall Engagement Score: ${overallStats.engagementScore}%`,
        `Activities Completed: ${overallStats.activitiesCompleted} / ${overallStats.activitiesLaunched} (Total: ${overallStats.totalActivities})`,
        `Total Action Plans reported: ${overallStats.totalActionPlans} across ${overallStats.totalTopics} topics`,
        `Session Rating Given: ${overallStats.sessionRating} / 5`,
      ];

      stats.forEach((stat) => {
        doc.text(`• ${stat}`, margin + 5, yPos);
        yPos += 6;
      });

      yPos += 10;

      // === 2. SESSION-WISE PARTICIPATION ===
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('2. SESSION-WISE PARTICIPATION', margin, yPos);
      yPos += 10;

      sessionParticipation.forEach((session) => {
        checkPageBreak(60);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkColor);
        doc.text(`DAY ${session.day} – ${session.topic}`, margin, yPos);
        yPos += 7;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        session.activities.forEach((activity) => {
          let statusText = 'Not Launched';
          if (activity.status === 'completed') {
            statusText = 'Completed ✔';
          } else if (activity.status === 'not_attended') {
            statusText = 'Not Attended';
          }
          doc.text(`• ${activity.name}: ${statusText}`, margin + 5, yPos);
          yPos += 5;
        });

        yPos += 3;
        doc.setFont('helvetica', 'bold');
        doc.text(`${participant.name.split(' ')[0]}'s Key Takeaways:`, margin, yPos);
        yPos += 5;

        doc.setFont('helvetica', 'normal');
        session.keyTakeaways.forEach((takeaway) => {
          doc.text(`• ${takeaway}`, margin + 5, yPos);
          yPos += 5;
        });

        yPos += 8;
      });

      // === 3. PERSONAL CHALLENGES ===
      checkPageBreak(50);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text(`3. ${participant.name.split(' ')[0].toUpperCase()}'S PERSONAL CHALLENGES (Captured Topic-Wise)`, margin, yPos);
      yPos += 10;

      personalChallenges.forEach((item) => {
        checkPageBreak(25);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text(`Topic: ${item.topic}`, margin, yPos);
        yPos += 6;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...darkColor);
        item.challenges.forEach((challenge) => {
          const lines = doc.splitTextToSize(`• "${challenge}"`, contentWidth - 10);
          doc.text(lines, margin + 5, yPos);
          yPos += lines.length * 5;
        });

        yPos += 4;
      });

      yPos += 6;

      // === 4. PERSONAL ACTION PLAN ===
      checkPageBreak(50);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text(`4. ${participant.name.split(' ')[0].toUpperCase()}'S PERSONAL ACTION PLAN (Auto-Captured)`, margin, yPos);
      yPos += 10;

      personalActionPlans.forEach((item) => {
        checkPageBreak(25);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text(`Topic: ${item.topic}`, margin, yPos);
        yPos += 6;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...darkColor);
        item.actions.forEach((action) => {
          const lines = doc.splitTextToSize(`• ${action}`, contentWidth - 10);
          doc.text(lines, margin + 5, yPos);
          yPos += lines.length * 5;
        });

        yPos += 4;
      });

      // === 5. NEXT FOLLOW-UP ===
      checkPageBreak(35);
      yPos += 12;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${participant.name.split(' ')[0]}'s Next Follow-Up:`, margin, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`• 1st Review: +${nextFollowUp.firstReview.days} days (${nextFollowUp.firstReview.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`, margin + 5, yPos);
      yPos += 6;
      doc.text(`• 2nd Review: +${nextFollowUp.secondReview.days} days (${nextFollowUp.secondReview.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`, margin + 5, yPos);
      yPos += 6;

      // === 6. PERSONAL RECOMMENDATIONS ===
      checkPageBreak(40);
      yPos += 12;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('6. PERSONAL RECOMMENDATIONS', margin, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      personalRecommendations.forEach((rec) => {
        const recLines = doc.splitTextToSize(`• ${rec}`, contentWidth - 10);
        doc.text(recLines, margin + 5, yPos);
        yPos += recLines.length * 5 + 1;
      });

      // Footer on all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        // Bottom blue bar
        doc.setFillColor(...blueColor);
        doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');

        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text('Aktivity', pageWidth / 2, pageHeight - 5, { align: 'center' });
      }

      // Save
      doc.save(`Individual-Report-${participant.name.replace(/\s+/g, '-')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-lg font-semibold text-neutral-900">Individual Report</h1>
                <p className="text-sm text-neutral-500">{participant.program}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={generatePDF}
                disabled={isGeneratingPDF}
              >
                {isGeneratingPDF ? (
                  <>
                    <svg className="w-4 h-4 mr-2 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Report Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 print:py-0" ref={reportRef}>
        {/* Activity Overview Header */}
        <h1 className="text-2xl font-bold text-neutral-900 mb-6">Activity Overview</h1>

        {/* Report Title Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-t-4 border-blue-600 bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h1 className="text-xl font-bold text-neutral-900 mb-2">
            INDIVIDUAL PARTICIPANT REPORT – Engagement, Challenges & Action Plan Summary
          </h1>
          <p className="text-sm text-neutral-500 italic mb-6">
            (This is what each participant receives automatically)
          </p>

          {/* Participant Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span>🚀</span>
              <span className="font-medium">Participant Name:</span>
              <span className="font-bold text-neutral-900">{participant.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>📧</span>
              <span className="font-medium">Email:</span>
              <a href={`mailto:${participant.email}`} className="text-blue-600 hover:underline">{participant.email}</a>
            </div>
            <div className="flex items-center gap-2">
              <span>🏢</span>
              <span className="font-medium">Organization:</span>
              <span>{participant.organization}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>📋</span>
              <span className="font-medium">Program:</span>
              <span>{participant.program}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>👤</span>
              <span className="font-medium">Facilitator:</span>
              <span>{participant.facilitator}</span>
            </div>
          </div>
        </motion.div>

        {/* 1. Overall Participation Snapshot */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h2 className="text-lg font-bold text-neutral-900 mb-4">1. OVERALL PARTICIPATION SNAPSHOT</h2>
          <ul className="space-y-2">
            <li className="flex items-center gap-2">
              <span className="text-neutral-400">•</span>
              <span>Subsessions:</span>
              <span className="font-bold">{overallStats.modulesCount}</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-neutral-400">•</span>
              <span>Overall Engagement Score:</span>
              <span className="font-bold text-green-600">{overallStats.engagementScore}%</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-neutral-400">•</span>
              <span>Activities Completed:</span>
              <span className="font-bold">{overallStats.activitiesCompleted} / {overallStats.activitiesLaunched}</span>
              <span className="text-neutral-500 text-sm">(Total: {overallStats.totalActivities})</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-neutral-400">•</span>
              <span>Total Action Plans reported:</span>
              <span className="font-bold">{overallStats.totalActionPlans}</span>
              <span>across</span>
              <span className="font-bold">{overallStats.totalTopics} topics</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-neutral-400">•</span>
              <span>Session Rating Given:</span>
              <span className="font-bold">{overallStats.sessionRating} / 5</span>
              <span className="text-yellow-500">⭐</span>
            </li>
          </ul>
        </motion.div>

        {/* 2. Session-Wise Participation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h2 className="text-lg font-bold text-neutral-900 mb-4">2. SESSION-WISE PARTICIPATION</h2>

          {sessionParticipation.map((session, index) => (
            <div key={session.day} className={index > 0 ? 'mt-6 pt-6 border-t border-neutral-200' : ''}>
              <div
                className="flex items-center justify-between mb-3 cursor-pointer hover:bg-neutral-50 rounded-lg p-2 -ml-2 transition-colors"
                onClick={() => setSelectedSubSession(session)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-red-500">📍</span>
                  <h3 className="font-bold text-neutral-900">
                    Subsession {session.day} – <span className="italic">{session.topic}</span>
                  </h3>
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  {session.startedAt && (
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                      Started: {new Date(session.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {session.endedAt && (
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      Ended: {new Date(session.endedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              <ul className="space-y-1 mb-4 ml-6">
                {session.activities.map((activity, idx) => {
                  const isCompleted = activity.status === 'completed';
                  const isNotAttended = activity.status === 'not_attended';

                  let statusClass = 'text-neutral-500';
                  let statusText = 'Not Launched';

                  if (isCompleted) {
                    statusClass = 'text-green-600';
                    statusText = 'Completed ✔';
                  } else if (isNotAttended) {
                    statusClass = 'text-amber-600';
                    statusText = 'Not Attended';
                  }

                  return (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <span className="text-neutral-400">•</span>
                      <span>{activity.name}:</span>
                      <span className={statusClass}>{statusText}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="bg-neutral-50 rounded-lg p-4">
                <p className="font-semibold text-neutral-900 mb-2">
                  {participant.name.split(' ')[0]}'s Key Takeaways:
                </p>
                <ul className="space-y-1">
                  {session.keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                      <span className="text-neutral-400 mt-0.5">•</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </motion.div>

        {/* 3. Personal Challenges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h2 className="text-lg font-bold text-neutral-900 mb-4">
            3. {participant.name.split(' ')[0].toUpperCase()}'S PERSONAL CHALLENGES (Captured Topic-Wise)
          </h2>

          <div className="space-y-4">
            {personalChallenges.length > 0 ? (
              personalChallenges.map((item, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-orange-600 mb-2">Topic: {item.topic}</h4>
                  <ul className="space-y-1 ml-4">
                    {item.challenges.map((challenge, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                        <span className="text-neutral-400 mt-0.5">•</span>
                        <span>"{challenge}"</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <p className="text-neutral-500 text-sm italic">No challenges captured yet.</p>
            )}
          </div>
        </motion.div>

        {/* 4. Personal Action Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h2 className="text-lg font-bold text-neutral-900 mb-4">
            4. {participant.name.split(' ')[0].toUpperCase()}'S PERSONAL ACTION PLAN (Auto-Captured)
          </h2>

          <div className="space-y-4">
            {personalActionPlans.length > 0 ? (
              personalActionPlans.map((item, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-orange-600 mb-2">Topic: {item.topic}</h4>
                  <ul className="space-y-1 ml-4">
                    {item.actions.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                        <span className="text-neutral-400 mt-0.5">•</span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <p className="text-neutral-500 text-sm italic">No action plans captured yet.</p>
            )}
          </div>
        </motion.div>

        {/* 5. Next Follow-Up */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h2 className="text-lg font-bold text-neutral-900 mb-4">
            {participant.name.split(' ')[0].toUpperCase()}'S NEXT FOLLOW-UP:
          </h2>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-neutral-700">
              <span className="text-neutral-400">•</span>
              <span>
                <strong>1st Review:</strong> +{nextFollowUp.firstReview.days} days
                <span className="text-neutral-500 ml-2">
                  ({nextFollowUp.firstReview.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                </span>
              </span>
            </li>
            <li className="flex items-start gap-2 text-neutral-700">
              <span className="text-neutral-400">•</span>
              <span>
                <strong>2nd Review:</strong> +{nextFollowUp.secondReview.days} days
                <span className="text-neutral-500 ml-2">
                  ({nextFollowUp.secondReview.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                </span>
              </span>
            </li>
          </ul>
        </motion.div>

        {/* 6. Personal Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h2 className="text-lg font-bold text-neutral-900 mb-4">6. PERSONAL RECOMMENDATIONS</h2>
          <ul className="space-y-2">
            {personalRecommendations.map((rec, index) => (
              <li key={index} className="flex items-start gap-2 text-neutral-700">
                <span className="text-neutral-400">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </main>

      {/* Subsession Detail Modal */}
      <Modal
        isOpen={!!selectedSubSession}
        onClose={() => setSelectedSubSession(null)}
        title={`Subsession ${selectedSubSession?.day}: ${selectedSubSession?.topic}`}
        size="md"
      >
        {selectedSubSession && (
          <div className="space-y-4">
            {/* Timing Information */}
            <div className="bg-neutral-50 rounded-lg p-4">
              <h4 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Timing
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-500">Started</p>
                  <p className="font-medium text-green-700">
                    {selectedSubSession.startedAt
                      ? new Date(selectedSubSession.startedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                      : 'Not started'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Ended</p>
                  <p className="font-medium text-blue-700">
                    {selectedSubSession.endedAt
                      ? new Date(selectedSubSession.endedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                      : 'Not ended'}
                  </p>
                </div>
              </div>
              {selectedSubSession.startedAt && selectedSubSession.endedAt && (
                <div className="mt-3 pt-3 border-t border-neutral-200">
                  <p className="text-sm text-neutral-500">Duration</p>
                  <p className="font-medium text-neutral-900">
                    {Math.round((new Date(selectedSubSession.endedAt) - new Date(selectedSubSession.startedAt)) / 60000)} minutes
                  </p>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-500">Status:</span>
              <Badge
                variant={selectedSubSession.status === 'completed' ? 'success' : selectedSubSession.status === 'active' ? 'warning' : 'default'}
              >
                {selectedSubSession.status || 'Pending'}
              </Badge>
            </div>

            {/* Description */}
            {selectedSubSession.description && (
              <div>
                <h4 className="font-semibold text-neutral-900 mb-2">Description</h4>
                <p className="text-sm text-neutral-600">{selectedSubSession.description}</p>
              </div>
            )}

            {/* Activities */}
            <div>
              <h4 className="font-semibold text-neutral-900 mb-2">Activities ({selectedSubSession.activities.length})</h4>
              <ul className="space-y-2">
                {selectedSubSession.activities.map((activity, idx) => {
                  const isCompleted = activity.status === 'completed';
                  const isNotAttended = activity.status === 'not_attended';

                  return (
                    <li key={idx} className="flex items-center justify-between bg-neutral-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-neutral-700">{activity.name}</span>
                      {isCompleted ? (
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Completed
                        </span>
                      ) : isNotAttended ? (
                        <span className="text-xs text-amber-600 font-medium">Not Attended</span>
                      ) : (
                        <span className="text-xs text-neutral-500">Not Launched</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Key Takeaways */}
            {selectedSubSession.keyTakeaways.length > 0 && selectedSubSession.keyTakeaways[0] !== 'No takeaways recorded' && (
              <div>
                <h4 className="font-semibold text-neutral-900 mb-2">Your Key Takeaways</h4>
                <ul className="space-y-1">
                  {selectedSubSession.keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-neutral-600">
                      <span className="text-neutral-400 mt-0.5">•</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default IndividualReportPage;
